import React, { memo, useState, useMemo, useEffect } from 'react';
import { Col, Row } from 'react-bootstrap';
import { useGameEditMechanic } from 'src/hooks';
import { TDragDropTextGame } from "./types";
import GameNotFound from '../../../not-found';
import { handleChange } from 'src/helpers';
import { ButtonMotion, Uploader } from 'src/components';
import { TGameMechanicContent } from "src/@types";
import classnames from "classnames";
import { TouchBackend } from 'react-dnd-touch-backend';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { DndProvider } from 'react-dnd';
import { checkIsMobile } from '../../../../helpers';

// ========================================================================== //
// ================================= LOGIC ================================== //
// ========================================================================== //

/**
 * @description
 *    The calculation game component
 * @constructor
 */
function DragDropTextGame() {
  const {
    gameMechanic,
    gameId,
    updateGameMechanic,
  } = useGameEditMechanic();
  const [description, setDescription] = useState<string>('');
  const [descriptionSaved, setDescriptionSaved] = useState<Boolean>(true);
  const [descriptionArr, setDescriptionArr] = useState<string[]>([]);
  const [currentSelection, setCurrentSelection] = useState<number[]>([]);
  const [missingWords, setMissingWords] = useState<number[]>([]);
  const [uploaded, setUploadedImage] = useState<string | null>('');

  const isMobile = checkIsMobile();

  useEffect(() => {
    if (gameMechanic?.content) {
      const content = gameMechanic.content as TDragDropTextGame;
      setDescription(content?.sentence ?? '');
      setDescriptionArr(content?.sentence?.split(' ') || []);
      if (content?.missing_words) {
        setMissingWords(content.missing_words.map(word => word.index));
      }
    }
  }, [gameMechanic]);

  if (gameMechanic) {
    const onGameMechanicContentChange = (finalContent: TDragDropTextGame) => {
      updateGameMechanic({
        ...gameMechanic,
        content: finalContent as unknown as TGameMechanicContent
      });
    };

    const selectWords = ()=> {
      const sentenceList = (gameMechanic.content as TDragDropTextGame).sentence?.split(' ') ?? [];
      onGameMechanicContentChange({
        ...gameMechanic.content as TDragDropTextGame,
        missing_words: [...missingWords, ...currentSelection].map((indexNumber) => ({
          word: sentenceList?.[indexNumber] ?? '',
          index: indexNumber
        }))
      });
      setMissingWords(prev => [...prev, ...currentSelection]);
      setCurrentSelection([]);
    }

    const toggleSelection = (index: number) => {
      setCurrentSelection(prev => {
        if (prev.includes(index)) {
          return prev.filter(n => n !== index);
        } else {
          return [...prev, index];
        }
      });
    }

    const saveDescription = () => {
      onGameMechanicContentChange({
        ...gameMechanic.content as TDragDropTextGame,
        sentence: description,
        missing_words: []
      });
      setDescriptionSaved(true);
      setDescriptionArr(description.split(' '));
    };

    const handleImageUploader = (filePath: string) => {
      onGameMechanicContentChange({
        ...gameMechanic.content as TDragDropTextGame,
        filePath,
      });
      setUploadedImage(filePath);
    }

    useEffect(() => {
      const filePath = (gameMechanic.content as TDragDropTextGame)?.filePath;
      if (typeof filePath === 'string' && filePath.length > 0) {
        setUploadedImage(filePath);
      }
    }, [gameMechanic?.content]);

    const actionButtons = useMemo(() => {
      if (!descriptionSaved) return <ButtonMotion variant={'scramble'} disabled={description.length === 0}
                                                  onClick={saveDescription}>Save</ButtonMotion>;
      return (
        <>
          <ButtonMotion
            variant={'scramble'}
            onClick={() => setDescriptionSaved(false)}
          >
            Edit
          </ButtonMotion>
          <p />
          <ButtonMotion
            variant={'scramble'}
            onClick={selectWords}
          >
            Select Word
          </ButtonMotion>
        </>
      );
    }, [descriptionSaved, currentSelection, description]);

    return (
      <div className='highlight-word-editor'>
        <Row className='mb-3 mb-md-0'>
          <Col md={4}>
            <DndProvider backend={isMobile ? TouchBackend : HTML5Backend}>
              <Uploader
                gameId={gameId}
                currentUrl={uploaded}
                onGameMechanicContentChange={handleImageUploader}
                key={Math.random()}
              />
            </DndProvider>
          </Col>
          <Col md={8}>
            {descriptionSaved
              ? <div className='sentence-to-scramble text-area-style'>
                <p className="d-flex flex-wrap">{descriptionArr.map((word, i) =>
                <span key={`word-${i}`} onClick={() => toggleSelection(i)}
                      className={classnames({
                        selected: currentSelection.includes(i),
                        highlighted: missingWords.includes(i)
                      })}>{word}</span>)}</p></div> :
              <textarea value={description} onChange={handleChange(setDescription)}
                        placeholder='Enter here the correct sentence, then select the words desired and click on “select word” button to create a new selectable word.' />}
          </Col>
        </Row>
        <br />
        {actionButtons}
      </div>
    );
  }
  return <GameNotFound />;
}

export default memo(DragDropTextGame);
